import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Loader2, Flag } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

export function ProductsEditor({ b, onSaved }) {
  const [f, setF] = useState({ name: "", brand: "", price: "", in_stock: true });
  const call = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const add = (e) => {
    e.preventDefault();
    call(() => api.post(`/pro/businesses/${b.id}/products`, { ...f, brand: f.brand || null, price: f.price === "" ? null : +f.price }), "Produit ajouté")
      .then(() => setF({ name: "", brand: "", price: "", in_stock: true }));
  };
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="products-editor">
      <form onSubmit={add} className="grid gap-2 sm:grid-cols-[2fr_1fr_100px_auto_auto] sm:items-center">
        <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Produit (ex. Courroie lave-linge)" data-testid="product-name-input" />
        <Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="Marque" data-testid="product-brand-input" />
        <Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="Prix €" data-testid="product-price-input" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.in_stock} onCheckedChange={(v) => setF({ ...f, in_stock: v })} data-testid="product-stock-switch" />En stock</label>
        <Button type="submit" className="bg-[#1E40AF] hover:bg-[#1D4ED8] hover:text-white" data-testid="product-add-button">Ajouter</Button>
      </form>
      <ul className="divide-y divide-slate-100">
        {(b.products || []).map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid={`product-row-${p.id}`}>
            <span><b>{p.name}</b>{p.brand && <span className="text-slate-500"> · {p.brand}</span>}{p.price != null && <span className="text-slate-500"> · {p.price.toFixed(2)} €</span>}</span>
            <span className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs"><Switch checked={p.in_stock} onCheckedChange={(v) => call(() => api.put(`/pro/businesses/${b.id}/products/${p.id}`, { ...p, in_stock: v }))} data-testid={`product-toggle-stock-${p.id}`} />{p.in_stock ? "En stock" : "Épuisé"}</label>
              <button onClick={() => call(() => api.delete(`/pro/businesses/${b.id}/products/${p.id}`), "Produit retiré")} aria-label="Supprimer" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" /></button>
            </span>
          </li>
        ))}
        {!b.products?.length && <li className="py-3 text-sm text-slate-500">Aucun produit pour l'instant.</li>}
      </ul>
    </div>
  );
}

export function PhotosEditor({ b, onSaved }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/pro/businesses/${b.id}/photos`, fd);
      } catch (err) {
        toast.error(errMsg(err));
      }
    }
    setBusy(false);
    e.target.value = "";
    onSaved();
  };
  const remove = async (pid) => {
    try {
      await api.delete(`/pro/businesses/${b.id}/photos/${pid}`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="photos-editor">
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={upload} data-testid="photo-file-input" />
      <Button onClick={() => input.current?.click()} disabled={busy} variant="outline" className="gap-2" data-testid="photo-upload-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Ajouter des photos
      </Button>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(b.photos || []).map((p) => (
          <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100" data-testid={`photo-item-${p.id}`}>
            <img src={fileUrl(p.url)} alt="" className="h-full w-full object-cover" />
            <button onClick={() => remove(p.id)} className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 opacity-0 transition-opacity group-hover:opacity-100" aria-label="Supprimer" data-testid={`photo-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportButton({ rid, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const send = async () => {
    try {
      await api.post(`/reviews/${rid}/report`, { reason });
      toast.success("Avis signalé à l'administrateur");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (!open) {
    return <Button size="sm" variant="outline" className="gap-1 text-red-700" onClick={() => setOpen(true)} data-testid={`report-open-${rid}`}><Flag className="h-3.5 w-3.5" />Signaler un abus</Button>;
  }
  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-red-200 bg-red-50 p-3 sm:flex-row">
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif : faux avis, propos injurieux, client inconnu…" className="bg-white" data-testid={`report-reason-${rid}`} />
      <Button size="sm" variant="destructive" disabled={reason.trim().length < 3} onClick={send} data-testid={`report-submit-${rid}`}>Envoyer</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
    </div>
  );
}

export function ProReviews({ bid, newSince }) {
  const [reviews, setReviews] = useState([]);
  const [drafts, setDrafts] = useState({});
  const load = () => api.get("/pro/reviews").then((r) => setReviews(r.data.filter((x) => x.business_id === bid))).catch(() => {});
  useEffect(() => {
    load();
  }, [bid]); // eslint-disable-line react-hooks/exhaustive-deps
  const reply = async (rid) => {
    try {
      await api.post(`/reviews/${rid}/reply`, { reply: drafts[rid] || "" });
      toast.success("Réponse publiée");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (!reviews.length) return <p className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500">Aucun avis pour l'instant.</p>;
  return (
    <div className="space-y-3" data-testid="pro-reviews">
      {reviews.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">{r.author} · {r.rating}/5
            {newSince && r.created_at > newSince && <span className="rounded-full bg-[#D97706] px-2 py-0.5 text-xs text-white" data-testid={`new-label-${r.id}`}>Nouveau</span>}
            {r.verified && <span className="rounded-full bg-[#DCFCE7] px-2 py-0.5 text-xs text-[#14532D]">Avis vérifié</span>}
            {r.reported && <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700" data-testid={`reported-label-${r.id}`}>Signalé à l'administrateur</span>}
          </p>
          <p className="mt-1 text-sm text-slate-700">{r.comment}</p>
          <Textarea rows={2} className="mt-3" placeholder="Votre réponse publique" defaultValue={r.reply || ""} onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })} data-testid={`reply-input-${r.id}`} />
          <div className="mt-2 flex flex-wrap items-start gap-2">
            <Button size="sm" className="bg-slate-900 hover:bg-[#1E40AF] hover:text-white" onClick={() => reply(r.id)} data-testid={`reply-submit-${r.id}`}>Répondre</Button>
            {!r.reported && <ReportButton rid={r.id} onDone={load} />}
          </div>
        </div>
      ))}
    </div>
  );
}
